/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import styles from './ccf-disposition.module.scss';
import { CcfButton, CcfTypography } from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  getIsDispositionOpen,
  getDispositionData,
  getInteractionTabs,
  getActiveInteractionType,
  mapDigitalInteractionPlatform,
  resolveDispositionForInteraction,
  activeInteraction,
  updateFlowMsgIndex,
  updateCopilotAppSpaceArticle,
  getFinalSummary,
  setBlankAdaptiveCardDataForContact,
  updateActiveContact,
  getLiveConversationTopics,
} from '@nice-ccf/ui-components';
import CcfDispositionInteractionAccordionDetails from './templates/ccf-disposition-accordion-details';
import { dispositionInteractionActions } from './ccf-disposition-slice.tsx';
import CcfDispositionHeader from './templates/ccf-disposition-header';
import {
  DigitalChannelStatus,
  DigitalChannelType,
  MediaType,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import { callApi } from '../ccf-app-space/ccf-copilot/ccf-copilot-api';
import { processFinalSummary } from '../ccf-app-space/ccf-copilot/ccf-copilot-util';
/* eslint-disable-next-line */
export interface CcfDispositionInteractionProps {}

type keytype = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [text: string]: any;
};

const newObj: keytype = {};
let clickedResolved: boolean;

/**
 * Component displays dispositions with multiple options to mark it as resolved
 * @param props -CcfDispositionInteractionProps
 * @returns displays dispositions with multiple options to mark it as resolved
 * @example <CcfDispositionInteraction/>
 */
export function CcfDispositionInteraction(
  props: CcfDispositionInteractionProps
) {
  const dispatch = useDispatch();
  const {
    setDispositionResolution,
    toggleDispositionCard,
    setDispositionData,
    clearDispositionById,
  } = dispositionInteractionActions;

  const isDispositionOpen: boolean = useSelector(getIsDispositionOpen);
  const activeContact = useSelector(activeContactSelector);
  const isDispositionCompleted = { isDispositionCompleted: false }; //useSelector(getDispositionForActiveTab);
  const dispositionData = useSelector(getDispositionData);
  const interactionTabs = useSelector(getInteractionTabs) || {};
  const activeInteractionType = useSelector(getActiveInteractionType);
  const [isSaveDisabled, setSaveDisable] = useState<boolean>(true);
  const [selectedModel, setModel] = useState<any>({});
  const conversationTopics: string[] =
    useSelector(getLiveConversationTopics(activeContact?.id || '')) || [];
  const allTabs = Object.values(interactionTabs).map((el: any) =>
    mapDigitalInteractionPlatform(el.realExternalPlatformId)
  );

  useEffect(() => {
    dispatch(toggleDispositionCard(false));
  }, [activeContact?.customer?.firstName]);

  useEffect(() => {
    if (
      activeContact &&
      dispositionData[activeContact.id] &&
      !dispositionData[activeContact.id][activeInteractionType]
    ) {
      dispatch(
        setDispositionData({
          ...dispositionData,
          [activeContact?.id.toString() as string]: {
            ...dispositionData[activeContact.id],
            isResolved: false,
            [activeInteractionType]: {
              OUTCOME: { value: '' },
              'Follow Up': { value: '' },
            },
          },
        })
      );
    }
    dispatch(toggleDispositionCard(false));
  }, [activeInteractionType]);

  /**
   * Function to handle disposition toggle
   * @example handleDispositionToggle
   */
  const handleDispositionToggle = () => {
    dispatch(toggleDispositionCard(!isDispositionOpen));
  };

  /**
   * Function to handle on click of mark as reolved options
   * @example onClickMarkAsResolvedOption
   */
  const onClickMarkAsResolvedOption = () => {
    if (
      (activeContact?.channel === 'Chat' ||
        activeContact?.media === 'Voice' ||
        activeContact?.channel === 'Email') &&
      activeContact?.copilotAssist === 'Yes'
    ) {
      dispatch(setBlankAdaptiveCardDataForContact(activeContact?.id));
    }
    dispatch(toggleDispositionCard(false));
    dispatch(
      resolveDispositionForInteraction({
        id: activeContact?.id,
      })
    );
    dispatch(
      setDispositionResolution({
        contactId: activeContact?.id,
        isResolved: true,
      })
    );
    if (activeContact?.id && dispositionData[activeContact.id].isResolved) {
      dispatch(clearDispositionById(activeContact.id));
      if (activeContact?.media === MediaType.VOICE) {
        dispatch(
          updateActiveContact({
            id: activeContact?.id || '',
            updates: { contactStatus: VoiceContactStatus.RESOLVED },
          })
        );
      } else {
        dispatch(
          updateActiveContact({
            id: activeContact?.id || '',
            updates: { contactStatus: DigitalChannelStatus.RESOLVED },
          })
        );
      }
      const contactHistoryLocalStorage = JSON.parse(
        localStorage.getItem('contactHistoryLS') || '[]'
      );
      if (contactHistoryLocalStorage.length > 0) {
        const newContactforContactHistory = {
          id: 40555,
          time: new Date().toISOString(),
          customer: `${[
            activeContact?.customer?.firstName,
            activeContact?.customer?.middleName,
            activeContact?.customer?.lastName,
          ]
            .filter(Boolean)
            .join(' ')}`,
          status: activeContact?.contactStatus,
          skill: activeContact?.channel,
          disposition: activeContact?.contactStatus,
          channel: 'Usha-Routing-Queue',
        };
        const updatedContactHistoryArray = JSON.parse(
          JSON.stringify(contactHistoryLocalStorage)
        );
        updatedContactHistoryArray.unshift(newContactforContactHistory);
        localStorage.setItem(
          'contactHistoryLS',
          JSON.stringify(updatedContactHistoryArray)
        );
      }
    }
    if (activeContact?.channel === DigitalChannelType.CHAT) {
      dispatch(updateFlowMsgIndex(0));
    }
  };

  /**
   * Function to validate all fields for dispositions
   * @returns
   * @example - validateFields
   */
  const validateFields = (
    value: any,
    field: any,
    tab: any,
    mediaType: string
  ) => {
    const contactId: string = activeContact?.id || '';
    if (
      mediaType === MediaType.VOICE ||
      mediaType === MediaType.VIDEOCALL ||
      mediaType === MediaType.VOICEMAIL
    ) {
      const model: any = {};
      if (field === 'disposition') {
        model[field] = { value: value };
        setSaveDisable(false);
      } else {
        model[field] = { value: value };
      }

      if (field === 'disposition') {
        const filtered: any = (Object.keys(model) || []).filter(
          (item: any) =>
            item === 'disposition' && model[item]?.value === 'MarkAsResolved'
        );
        const isMarkAsResolved = !!(filtered && filtered[0]?.length > 0);
        const data: any = { ...dispositionData[contactId] };
        const tabData: any = { ...data[tab] };
        const copyData: any = { ...tabData, ...model };
        const dispositionTabData: any = { ...data, [tab]: copyData };
        dispatch(
          setDispositionData({
            ...dispositionData,
            [activeContact?.id.toString() as string]: {
              ...dispositionTabData,
              isResolved: isMarkAsResolved,
            },
          })
        );
      } else {
        const data: any = { ...dispositionData[contactId] };
        const tabData: any = { ...data[tab] };
        const copyData: any = { ...tabData, ...model };
        const dispositionTabData: any = { ...data, [tab]: copyData };
        dispatch(
          setDispositionData({
            ...dispositionData,
            [activeContact?.id.toString() as string]: {
              ...dispositionTabData,
            },
          })
        );
      }
    }
    if (mediaType === MediaType.DIGITAL) {
      if (value) {
        const localModel = { ...selectedModel };
        if (Object.keys(localModel).length === 0) {
          (allTabs || []).forEach((tab: any) => {
            localModel[tab.tabName] = '';
          });
        }
        if (field !== 'all') {
          localModel[field] = value;
        } else {
          (Object.keys(localModel) || []).forEach((key: string) => {
            localModel[key] = value;
          });
        }
        setModel({ ...localModel });
        const ismarkedAsResolved: boolean = (
          Object.values(localModel) || []
        ).some((item: any) => !item || item === 'Pending');
        if (!ismarkedAsResolved) {
          const data: any = { ...dispositionData[contactId] };
          const newModel: any = {};
          (Object.keys(localModel) || []).forEach((x: any) => {
            newModel[x] = { value: localModel[x] };
          });
          const dispositionTabData: any = { ...data, [tab]: newModel };
          dispatch(
            setDispositionData({
              ...dispositionData,
              [activeContact?.id.toString() as string]: {
                ...dispositionTabData,
                isResolved: true,
              },
            })
          );
        }
        setSaveDisable(false);
        if (activeContact?.copilotSource === 'Live LLM') {
          //final summary callApi
          const requestBody = {
            agentId: '1898',
            tenantId: '12345678',
            contactId: activeContact?.id,
            channel: 'digital',
            isConversationResolved: true,
            busNo: '12345678',
          };

          new Promise<void>((resolve, reject) => {
            callApi(
              'https://1tx1doz6q5.execute-api.us-west-2.amazonaws.com/dev/agentcopilotapi/v1/final-summary',
              'POST',
              requestBody
            )
              .then((response: any) => {
                // setDispositionData - for final summary/disposition notes
                processFinalSummary(
                  response.data,
                  dispatch,
                  conversationTopics,
                  activeContact?.id
                );
                resolve();
              })
              .catch((error) => {
                console.error(
                  'There was a problem with the fetch operation from disposition 245:',
                  error
                );
                reject(error);
              });
          });
        }
      }
    }
    clickedResolved = !clickedResolved;
  };

  return (
    <section>
      <div
        className={`${styles.dispositionsDetails} ${
          isDispositionOpen
            ? styles.expandedDisposition
            : styles.collapsedDisposition
        }`}
        id="agent_outcomes"
      >
        <div className="dispositionsDetailsWrapper">
          <div
            className="dispositionsDetailsHeader"
            onClick={handleDispositionToggle}
          >
            <CcfDispositionHeader
              isDispositionCompleted={
                isDispositionCompleted?.isDispositionCompleted
              }
              isDispositionOpen={isDispositionOpen}
              activeContact={activeContact}
            />
          </div>
          {isDispositionOpen && (
            <>
              <div className="displayAccordionDetails">
                {((activeContact?.media === MediaType.DIGITAL &&
                  activeContact?.channel !== 'Chat') ||
                  (activeContact?.channel === 'Chat' &&
                    activeContact?.copilotAssist === 'No')) && (
                  <CcfDispositionInteractionAccordionDetails
                    clickedResolved={clickedResolved}
                    contactId={activeContact?.id}
                    contactMedia={activeContact?.media}
                    allTabs={allTabs}
                    type="media"
                    validateFields={validateFields}
                  />
                )}
                {((activeContact?.media === MediaType.VOICE &&
                  activeContact?.copilotAssist === 'Yes') ||
                  (activeContact?.media === MediaType.DIGITAL &&
                    activeContact?.channel === 'Chat' &&
                    activeContact?.copilotAssist === 'Yes')) && (
                  <CcfDispositionInteractionAccordionDetails
                    clickedResolved={clickedResolved}
                    contactId={activeContact?.id}
                    contactMedia={activeContact?.media}
                    copilotAssist={activeContact?.copilotAssist}
                    allTabs={allTabs}
                    type="media"
                    validateFields={validateFields}
                  />
                )}
                {activeContact?.media === MediaType.VOICE &&
                  allTabs &&
                  Object.keys(interactionTabs).length > 0 && (
                    <CcfDispositionInteractionAccordionDetails
                      clickedResolved={clickedResolved}
                      contactId={activeContact?.id}
                      contactMedia={MediaType.DIGITAL}
                      allTabs={allTabs}
                      type="media"
                      validateFields={validateFields}
                    />
                  )}
                {activeContact?.media === MediaType.VIDEOCALL &&
                  allTabs &&
                  Object.keys(interactionTabs).length > 0 && (
                    <CcfDispositionInteractionAccordionDetails
                      clickedResolved={clickedResolved}
                      contactId={activeContact?.id}
                      contactMedia={MediaType.VIDEOCALL}
                      allTabs={[]}
                      type="media"
                      validateFields={validateFields}
                    />
                  )}
                {activeContact?.media === MediaType.VOICEMAIL &&
                  allTabs &&
                  Object.keys(interactionTabs).length > 0 && (
                    <CcfDispositionInteractionAccordionDetails
                      clickedResolved={clickedResolved}
                      contactId={activeContact?.id}
                      contactMedia={MediaType.VOICEMAIL}
                      allTabs={[]}
                      type="media"
                      validateFields={validateFields}
                    />
                  )}
              </div>
              <div className="markAsResolved">
                <CcfButton
                  id="agent_outcomes_save"
                  className={`${
                    isSaveDisabled ? styles.disabled : ''
                  } markAsResolvedButton`}
                  variant="contained"
                  color="inherit"
                  onClick={onClickMarkAsResolvedOption}
                  disabled={isSaveDisabled}
                >
                  <CcfTypography
                    variant="inherit"
                    className="markAsResolvedText"
                    translationKey="save"
                  ></CcfTypography>
                </CcfButton>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
export default CcfDispositionInteraction;
